import { StatusMessage } from "@brexhq/component-library";
import styled from "@emotion/styled";
import { position } from "polished";
import * as React from "react";

import { TitleM } from "@brexhq/shared/components/Typography/Components";

export type ErrorBoundaryState = {
  error: Error | null;
};

export type ErrorBoundaryErrorRendererArgs = Pick<
  ErrorBoundaryState,
  "error"
> & {
  resetError: () => void;
};

export type ErrorBoundaryProps = {
  children: React.ReactNode;
  onError?: (error: Error, info: React.ErrorInfo) => void;
  renderError?: (arg0: ErrorBoundaryErrorRendererArgs) => React.ReactNode;
};

export const FullScreenPageContainer = styled.div({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  overflow: "auto",
  scrollBehavior: "smooth",
  ...position("absolute", 0, 0, 0, 0),
});

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state = { error: null };

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log error to console instead of Sentry
    console.error("ERROR COMPONENT SEEN: ErrorBoundary", error, info);
    if (this.props.onError) {
      this.props.onError(error, info);
    }
    this.setState({ error });
  }

  resetError = () => {
    this.setState({ error: null });
  };

  render() {
    const {
      children,
      renderError = () => (
        <FullScreenPageContainer>
          <StatusMessage
            graphic="https://brand.brex.com/transform/bb640634-d6e4-4850-bc8d-b07d66316f32/Back-end-Crash_00000"
            title={<TitleM>{"Unexpected error."}</TitleM>}
            description="Please refresh your page to try again. If the problem persists, contact support@brex.com"
          />
        </FullScreenPageContainer>
      ),
    } = this.props;

    return this.state.error
      ? renderError({ resetError: this.resetError, error: this.state.error })
      : children;
  }
}
